import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectCheckboxValues, toggleCheckbox } from "../redux/slices/checkboxSlice";
import Tooltip from "./Tooltip";
import { XCircle } from "react-bootstrap-icons"; // Import clear icon

const CheckBoxGroup = ({
  label,
  tooltip,
  description,
  stateName,
  options,
  hint = "Multiple Selection",
  initialDisplayCount = 10,
  customTopOptions = [],
  allowOther = false,
  enableSearch = false,
  errorProp,
}) => {
  const dispatch = useDispatch();
  const selectedValues = useSelector((state) => selectCheckboxValues(state, stateName));

  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [customOption, setCustomOption] = useState(""); // User input for "Other"
  const [error, setError] = useState(false); // Error state for validation
  const [expanded, setExpanded] = useState(false);
  
  const MAX_CHAR_COUNT = 180;

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const lastValue = options.length > 0 ? Math.max(...options.map(o => parseInt(o.value, 10))) : 0;
  const customValue = lastValue + 1;

  // Memoize "Other" option
  const otherOption = useMemo(() => (allowOther ? { label: "Other", value: customValue } : null), [allowOther, customValue]);

  // Memoize all options (predefined + "Other" if enabled)
  const allOptions = useMemo(() => [...options, ...(otherOption ? [otherOption] : [])], [options, otherOption]);

  // Ensure top options stay on top even after expanding
  const topOptions = allOptions.filter(opt => customTopOptions.includes(parseInt(opt.value, 10)));
  const remainingOptions = allOptions.filter(opt => !customTopOptions.includes(parseInt(opt.value, 10)));

  const displayedOptions = showAll
    ? [...topOptions, ...remainingOptions]
    : [...topOptions, ...remainingOptions.slice(0, initialDisplayCount - topOptions.length)];

  // Filter options based on search term
  const filteredOptions = searchTerm
    ? allOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : displayedOptions;

  // Ensure "Other" is always visible even when no match is found
  if (allowOther && !filteredOptions.includes(otherOption)) {
    filteredOptions.push(otherOption);
  }

  // Handle checkbox toggle
  const handleCheckboxChange = (event) => {
    const value = parseInt(event.target.value, 10);
    dispatch(toggleCheckbox({ stateName, value }));

    // Reset error when deselecting "Other"
    if (value === otherOption?.value && !event.target.checked) {
      setCustomOption("");
      setError(false);
    }
  };

  // Handle input validation for "Other"
  const handleCustomOptionChange = (event) => {
    const trimmedValue = event.target.value.trim();
    setCustomOption(trimmedValue);

    // Validate: Show error if empty
    if (selectedValues.includes(otherOption?.value) && trimmedValue === "") {
      setError(true);
    } else {
      setError(false);
    }

    if (selectedValues.includes(otherOption?.value)) {
      dispatch(toggleCheckbox({ stateName, value: otherOption.value })); // Remove "Other"
      dispatch(toggleCheckbox({ stateName, value: otherOption.value })); // Add it back
    }
  };

  // Function to highlight matched text
  const highlightMatch = (text) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, "gi");
    return text.replace(regex, "<span style='color: red; font-weight: bold;'>$1</span>");
  };

  return (
    <div data-field={stateName} className={`mt-3 ${errorProp ? "thick-error-border" : "radio-group-container"}`}>
      <fieldset className="form-section">
        <legend className="mb-3 fw-bold">
          {label}
          {tooltip && <Tooltip text={tooltip} />}
          <span className="badge bg-primary ms-2" style={{ fontSize: "0.7rem" }}>{hint}</span>
        </legend>

        {description && (
          <div
            className="text-muted mb-3 ps-2 border-start border-3 border-secondary"
            style={{ fontSize: "0.95rem", lineHeight: "1.6"}}
          >
          <span dangerouslySetInnerHTML={{ __html: expanded ? description : description.slice(0, MAX_CHAR_COUNT) + (description.length > MAX_CHAR_COUNT ? "..." : "") }} />
            {description.length > MAX_CHAR_COUNT && (
              <button 
                className="btn btn-link p-0 ms-1"
                style={{ fontSize: "0.9rem", textDecoration: "none" }}
                onClick={toggleExpand}
              >
                <em>{expanded ? "Show Less" : "Show More"}</em>
              </button>
            )}
          </div>
        )}

        {/* Search Box (Only if enabled) */}
        {enableSearch && (
          <div className="input-group mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>
                <XCircle size={18} />
              </button>
            )}
          </div>
        )}

        <div className="row">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(({ label, value }) => (
              <div className="col-6 col-md-4 col-lg-3" key={value}>
                <div className="form-check custom-radio">
                  <input
                    type="checkbox"
                    className="form-check-input custom-radio-input"
                    id={`${stateName}-${value}`}
                    value={value}
                    checked={selectedValues.includes(parseInt(value, 10))}
                    onChange={handleCheckboxChange}
                  />
                  <label
                    className="form-check-label custom-radio-label"
                    htmlFor={`${stateName}-${value}`}
                    dangerouslySetInnerHTML={{ __html: highlightMatch(label) }}
                  />
                </div>

                {/* Display input box when "Other" is selected */}
                {allowOther && value === otherOption?.value && selectedValues.includes(value) && (
                  <div className="mt-2">
                    <input
                      type="text"
                      className={`form-control ${error ? "border-danger" : ""} ${errorProp ? "thick-error-border" : ""}`}
                      value={customOption}
                      onChange={handleCustomOptionChange}
                      placeholder="Enter your own option"
                      data-field={`${stateName}-custom`}
                    />
                    {(error || errorProp) && <small className="text-danger">* This field is required.</small>}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-primary">Not found</p>
          )}
        </div>

        {/* Show More/Less button (Hidden while searching) */}
        {!searchTerm && allOptions.length > initialDisplayCount && (
          <div className="mt-2">
            <button className="btn btn-link p-0" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show Less" : "Show More"}
            </button>
          </div>
        )}
      </fieldset>
    </div>
  );
};

export default CheckBoxGroup;
